/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { CalendarDays, CalendarRange, Droplets, Zap } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import components */
import DashboardGrid from '@/components/layout/DashboardGrid';
import SystemStatisticsTable, {
    type BuildingStatisticsSeriesType,
} from '@/components/tabs/overview/system/SystemStatisticsTable';
import SystemStatisticsChart, {
    getAvailableSystemStatisticsChartSupplyKeys,
    type SystemStatisticsChartMode,
    type SystemStatisticsChartSupplyKeys,
} from '@/components/tabs/overview/system/SystemStatisticsChart';
import { useBuildingStatistics } from '@/components/tabs/overview/system/useBuildingStatistics';

const CHART_SUPPLY_LABELS: Record<SystemStatisticsChartSupplyKeys[number], string> = {
    pv: 'Photovoltaics',
    grid_feed: 'Grid feed',
    grid_supply: 'Electricity',
    heat: 'Heat',
    water: 'Water',
    gas: 'Gas',
};

function SystemStatisticsTab() {
    const { locale, t } = useI18n();
    const { systemKey } = useParams<{ systemKey: string }>();
    const [seriesType, setSeriesType] = useState<BuildingStatisticsSeriesType>('yearly_W');
    const [chartMode, setChartMode] = useState<SystemStatisticsChartMode>('energy');
    const { errorMessage, isLoading, statistics } = useBuildingStatistics(systemKey);
    const availableChartSupplyKeys = useMemo(
        () => getAvailableSystemStatisticsChartSupplyKeys(statistics, seriesType, chartMode),
        [chartMode, seriesType, statistics],
    );
    const [selectedChartSupplyKeys, setSelectedChartSupplyKeys] = useState<SystemStatisticsChartSupplyKeys>([]);

    useEffect(() => {
        setSelectedChartSupplyKeys(availableChartSupplyKeys);
    }, [availableChartSupplyKeys]);

    if (!systemKey) {
        return <Navigate to={APP_ROUTES.systems} replace />;
    }

    return (
        <DashboardGrid
            desktopCols={["1fr", "1fr"]}
            desktopRows={["auto", "auto", "3fr", "1fr"]}
            breakpoint={"2xl"}
            aria-label={t('Statistic overview')}
            mobileMinHeight={"150px"}
        >
            <DashboardGrid.Item
                className="grid min-h-0"
                desktopColSpan={2}
            >
                <h2>{t('Statistics')}</h2>
                <p>{t('Annual electricity, heat, water consumption and photovoltaic generation calculated from meter reading differences for system {key}.', { key: systemKey })}</p>
            </DashboardGrid.Item>

            <DashboardGrid.Item as="section" className="dashboard-panel flex items-center justify-start p-5" desktopColSpan={2}>
                <div className={`grid w-full grid-cols-1 gap-y-4 text-left md:divide-x md:divide-base-content ${
                    availableChartSupplyKeys.length > 0 ? 'md:grid-cols-3' : 'md:grid-cols-2'
                }`}>
                    <div className="flex items-center gap-3 md:pr-6">
                        <span className="text-sm font-semibold">{t('Display')}</span>
                        <label className="toggle border border-base-300 bg-base-200 text-base-content">
                            <input
                                type="checkbox"
                                checked={seriesType === 'monthly_W'}
                                onChange={(event) => {
                                    setSeriesType(event.target.checked ? 'monthly_W' : 'yearly_W');
                                }}
                                aria-label={t('Display')}
                            />
                            <CalendarDays className="h-4 w-4" aria-hidden="true" />
                            <CalendarRange className="h-4 w-4" aria-hidden="true" />
                        </label>
                        <span className="w-24 text-sm">
                            {seriesType === 'yearly_W' ? t('Yearly') : t('Monthly')}
                        </span>
                    </div>

                    <div className="flex items-center gap-3 md:px-6">
                        <span className="text-sm font-semibold">{t('Data type')}</span>
                        <label className="toggle border border-base-300 bg-base-200 text-base-content">
                            <input
                                type="checkbox"
                                checked={chartMode === 'volume'}
                                onChange={(event) => {
                                    setChartMode(event.target.checked ? 'volume' : 'energy');
                                }}
                                aria-label={t('Data type')}
                            />
                            <Zap className="h-4 w-4" aria-hidden="true" />
                            <Droplets className="h-4 w-4" aria-hidden="true" />
                        </label>
                        <span className="w-24 text-sm">
                            {chartMode === 'energy' ? t('Energy') : t('Volume')}
                        </span>
                    </div>

                    {availableChartSupplyKeys.length > 0 ? (
                        <div className="flex items-center gap-3 md:pl-6">
                            <label className="text-sm font-semibold" htmlFor="system-statistics-supply-types-select">
                                {t('Supply types')}:
                            </label>
                            <select
                                multiple
                                id="system-statistics-supply-types-select"
                                className="select select-bordered scrollbar-hidden h-12 w-auto"
                                value={selectedChartSupplyKeys}
                                onChange={(event) => {
                                    setSelectedChartSupplyKeys(Array.from(
                                        event.currentTarget.selectedOptions,
                                        (option) => option.value as SystemStatisticsChartSupplyKeys[number],
                                    ));
                                }}
                            >
                                {availableChartSupplyKeys.map((supplyKey) => (
                                    <option key={supplyKey} value={supplyKey}>
                                        {t(CHART_SUPPLY_LABELS[supplyKey])}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ) : null}
                </div>
            </DashboardGrid.Item>

            {/* Scrollable panels require a matching dashboard path in src/config/appPageLayout.ts. */}
            <DashboardGrid.Item as="section" className="dashboard-panel items-start justify-start" desktopRowSpan={2} mobileMinHeight={"500px"} scrollable>
                <SystemStatisticsTable
                    seriesType={seriesType}
                    statistics={statistics}
                    targetUnits={{
                        pv: 'MWh',
                        grid_feed: 'MWh',
                        grid_supply: 'MWh',
                        heat: 'MWh',
                        water: 'm³',
                        gas: 'm³',
                    }}
                    errorMessage={errorMessage}
                    isLoading={isLoading}
                    fractionDigits={{
                        pv: 2,
                        grid_feed: 2,
                        grid_supply: 2,
                        heat: 2,
                        water: 1,
                        gas: 1,
                    }}
                />
            </DashboardGrid.Item>

            <DashboardGrid.Item as="section" className="dashboard-panel">
                <SystemStatisticsChart
                    seriesType={seriesType}
                    statistics={statistics}
                    targetUnits={{
                        ...(chartMode === 'energy'
                            ? { pv: 'MWh', grid_feed: 'MWh', grid_supply: 'MWh', heat: 'MWh' }
                            : { water: 'm³', gas: 'm³' }),
                    }}
                    errorMessage={errorMessage}
                    isLoading={isLoading}
                    mode={chartMode}
                    supplyKeys={selectedChartSupplyKeys}
                    locale={locale}
                    t={t}
                />
            </DashboardGrid.Item>

            <DashboardGrid.Item as="section" className="dashboard-panel">
                <span>Box4</span>
            </DashboardGrid.Item>
        </DashboardGrid>
    );
}

export default SystemStatisticsTab;
